import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@humansignal/ui";
import { useAPI } from "../../providers/ApiProvider";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./SaveTemplateModal.prefix.css";

const block = cn("save-template-modal");

/**
 * Modal for saving an existing project as a reusable template.
 * Captures a name, optional description, and visibility — one of:
 *   - "org"      (only the active org, default)
 *   - "specific" (active org + a picked list of other orgs)
 *   - "platform" (everyone, staff-only)
 */
export const SaveTemplateModal = ({ open, project, onClose, onSaved, isStaff }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("org");
  const [sharedIds, setSharedIds] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset state each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setName(project?.title ? `${project.title} template` : "");
    setDescription("");
    setVisibility("org");
    setSharedIds([]);
    setError(null);
  }, [open, project?.title]);

  // Load candidate orgs for the multi-select — staff sees all, non-staff
  // sees only orgs they belong to (so they don't accidentally share into
  // orgs they can't access).
  useEffect(() => {
    if (!open) return;
    (async () => {
      const endpoint = isStaff ? "platformOrgs" : "organizations";
      const params = isStaff ? { scope: "owned", page_size: 200 } : {};
      const res = await api.callApi(endpoint, { params });
      if (res && !res.error) {
        const list = Array.isArray(res) ? res : (res.results ?? []);
        setOrgs(list);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isStaff]);

  const canUsePlatform = !!isStaff;

  // Combobox state for the "share with" picker. We keep this here rather
  // than as sub-component so hooks order stays stable regardless of the
  // selected visibility option.
  const [orgSearch, setOrgSearch] = useState("");
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);

  const toggleOrg = useCallback((id) => {
    setSharedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const removeOrg = useCallback((id) => {
    setSharedIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const submit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!name.trim()) {
        setError("Template name is required.");
        return;
      }
      setSaving(true);
      setError(null);
      const body = {
        name: name.trim(),
        description: description.trim(),
        visibility,
      };
      if (visibility === "specific") {
        body.shared_with_organizations = sharedIds;
      }
      const res = await api.callApi("projectSaveAsTemplate", {
        params: { pk: project.id },
        body,
      });
      setSaving(false);
      if (!res || res.error) {
        setError(res?.error ?? "Failed to save template");
        return;
      }
      onSaved?.(res);
      onClose?.();
    },
    [api, name, description, visibility, sharedIds, project, onSaved, onClose],
  );

  // The user's "home" org is implicitly included — filter it out of the
  // picker so they don't share into their own org redundantly.
  // Keep these hooks ABOVE the ``!open`` early return so the hook count
  // stays stable across renders (open/closed).
  const activeOrgId = project?.organization;
  const pickable = useMemo(() => orgs.filter((o) => o.id !== activeOrgId), [orgs, activeOrgId]);
  const selectedOrgs = useMemo(() => pickable.filter((o) => sharedIds.includes(o.id)), [pickable, sharedIds]);
  const searchResults = useMemo(() => {
    const q = orgSearch.trim().toLowerCase();
    const available = pickable.filter((o) => !sharedIds.includes(o.id));
    if (!q) return available.slice(0, 25);
    return available.filter((o) => o.title?.toLowerCase().includes(q)).slice(0, 25);
  }, [pickable, sharedIds, orgSearch]);

  if (!open) return null;

  return (
    <div className={block.elem("overlay").toClassName()} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <form className={block.toClassName()} onSubmit={submit}>
        <h2 className={block.elem("title").toClassName()}>
          {t("pages.settings.save_template_modal.save_project_as_template")}
        </h2>
        <p className={block.elem("subtitle").toClassName()}>
          Snapshots the labeling config, instructions, sampling, and annotation rules so you (or others) can spin up an
          identical project in one click.
        </p>

        <label className={block.elem("field").toClassName()}>
          <span className={block.elem("field-label").toClassName()}>Name</span>
          <input
            type="text"
            className={block.elem("input").toClassName()}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("pages.settings.save_template_modal.eg_hausa_ner_v2")}
            autoFocus
            maxLength={200}
          />
        </label>

        <label className={block.elem("field").toClassName()}>
          <span className={block.elem("field-label").toClassName()}>
            Description <span className={block.elem("optional").toClassName()} />
          </span>
          <textarea
            className={block.elem("textarea").toClassName()}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("pages.settings.save_template_modal.when_should_someone_use_this_template")}
            rows={3}
            maxLength={2000}
          />
        </label>

        <div className={block.elem("field").toClassName()}>
          <span className={block.elem("field-label").toClassName()}>
            {t("pages.settings.save_template_modal.who_can_use_this_template")}
          </span>
          <div role="radiogroup" className={block.elem("visibility").toClassName()}>
            <label className={block.elem("option").toClassName()}>
              <input
                type="radio"
                name="__vis"
                value="org"
                checked={visibility === "org"}
                onChange={() => setVisibility("org")}
              />
              <div>
                <strong>{t("pages.settings.save_template_modal.my_organization_only")}</strong>
                <small>{t("pages.settings.save_template_modal.members_of_your_active_org")}</small>
              </div>
            </label>

            <label className={block.elem("option").toClassName()}>
              <input
                type="radio"
                name="__vis"
                value="specific"
                checked={visibility === "specific"}
                onChange={() => setVisibility("specific")}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong>{t("pages.settings.save_template_modal.my_org_specific_organizations")}</strong>
                <small>{t("pages.settings.save_template_modal.pick_additional_orgs_below")}</small>

                {visibility === "specific" && (
                  <div className={block.elem("share-inline").toClassName()} onClick={(e) => e.stopPropagation()}>
                    <span className={block.elem("share-inline-label").toClassName()}>
                      {t("pages.settings.save_template_modal.share_with")}
                    </span>
                    {pickable.length === 0 ? (
                      <p className={block.elem("empty").toClassName()}>
                        No other organizations available to share with.
                      </p>
                    ) : (
                      <div className={block.elem("combobox").toClassName()}>
                        <div className={block.elem("combo-shell").toClassName()}>
                          {selectedOrgs.map((o) => (
                            <span key={o.id} className={block.elem("chip").toClassName()}>
                              {o.title}
                              <button
                                type="button"
                                className={block.elem("chip-x").toClassName()}
                                onClick={() => removeOrg(o.id)}
                                aria-label={`Remove ${o.title}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <input
                            type="search"
                            className={block.elem("combo-input").toClassName()}
                            placeholder={selectedOrgs.length ? "Add more…" : "Search organizations…"}
                            value={orgSearch}
                            onChange={(e) => {
                              setOrgSearch(e.target.value);
                              setOrgMenuOpen(true);
                            }}
                            onFocus={() => setOrgMenuOpen(true)}
                            onBlur={() => setTimeout(() => setOrgMenuOpen(false), 120)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {orgMenuOpen && (
                          <div className={block.elem("combobox-menu").toClassName()}>
                            {searchResults.length === 0 ? (
                              <div className={block.elem("combobox-empty").toClassName()}>
                                {orgSearch.trim() ? "No matches" : "All organizations added"}
                              </div>
                            ) : (
                              searchResults.map((o) => (
                                <button
                                  key={o.id}
                                  type="button"
                                  className={block.elem("combobox-item").toClassName()}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    toggleOrg(o.id);
                                    setOrgSearch("");
                                  }}
                                >
                                  {o.title}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </label>

            <label className={block.elem("option").mod({ disabled: !canUsePlatform }).toClassName()}>
              <input
                type="radio"
                name="__vis"
                value="platform"
                checked={visibility === "platform"}
                onChange={() => setVisibility("platform")}
                disabled={!canUsePlatform}
              />
              <div>
                <strong>{t("pages.settings.save_template_modal.platformwide")}</strong>
                <small>
                  {canUsePlatform ? "Everyone on the platform" : "Only platform staff can publish platform-wide"}
                </small>
              </div>
            </label>
          </div>
        </div>

        {error && <div className={block.elem("error").toClassName()}>{error}</div>}

        <div className={block.elem("actions").toClassName()}>
          <Button type="button" look="outlined" variant="neutral" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" look="filled" variant="primary" waiting={saving} disabled={saving || !name.trim()}>
            {saving ? "Saving…" : "Save template"}
          </Button>
        </div>
      </form>
    </div>
  );
};
